import { widget } from './prefsDefaults';
import type { PerfilTipo, WidgetConfig } from './types';

/** Widgets por defecto de cada perfil (ids estables; las series son claves reales de los datos). */
const serie = (id: string, rubro: string, categoria: string, presentacion: string | null) =>
  widget(id, 'serie', { rubro, categoria, presentacion });
const comparador = (id: string, rubro: string, categoria: string, presentacion: string | null) =>
  widget(id, 'comparador', { rubro, categoria, presentacion });
const movers = (id: string, rubro: string, periodoDias = 7) =>
  widget(id, 'movers', { rubro, periodoDias });
const calculadora = (tab: 'conversor' | 'lote' | 'leche'): WidgetConfig =>
  widget(`w-calculadora-${tab}`, 'calculadora', { tab });
const bcv = (): WidgetConfig => widget('w-bcv', 'bcv');
const alertas = (): WidgetConfig => widget('w-alertas', 'alertas');
const favoritos = (): WidgetConfig => widget('w-favoritos', 'favoritos');
const fuentes = (visible = true): WidgetConfig => widget('w-fuentes', 'fuentes', {}, visible);
const noticias = (): WidgetConfig => widget('w-noticias', 'noticias');
const resumen = (visible = true): WidgetConfig => widget('w-resumen', 'resumen', {}, visible);

const PLANTILLAS: Record<PerfilTipo, () => WidgetConfig[]> = {
  ganadero_carne: () => [
    bcv(),
    serie('w-toro-pie', 'bovino', 'toro', 'en_pie'),
    noticias(),
    serie('w-maute-pie', 'bovino', 'maute', 'en_pie'),
    serie('w-novillo-canal', 'bovino', 'novillo', 'en_canal'),
    movers('w-movers-bovino', 'bovino'),
    comparador('w-comparador-toro', 'bovino', 'toro', 'en_pie'),
    alertas(),
    calculadora('lote'),
    favoritos(),
    fuentes(false),
  ],
  lechero: () => [
    bcv(),
    serie('w-leche-caliente', 'lacteos', 'leche_vaca', 'caliente'),
    noticias(),
    serie('w-leche-fria', 'lacteos', 'leche_vaca', 'fria'),
    serie('w-leche-queseras', 'lacteos', 'leche_vaca', 'queseras'),
    serie('w-vaca-pie', 'bovino', 'vaca', 'en_pie'),
    comparador('w-comparador-leche', 'lacteos', 'leche_vaca', 'fria'),
    movers('w-movers-lacteos', 'lacteos'),
    calculadora('leche'),
    alertas(),
    favoritos(),
  ],
  quesero: () => [
    bcv(),
    serie('w-queso', 'lacteos', 'queso', null),
    noticias(),
    serie('w-leche-queseras', 'lacteos', 'leche_vaca', 'queseras'),
    serie('w-leche-fria', 'lacteos', 'leche_vaca', 'fria'),
    serie('w-leche-caliente', 'lacteos', 'leche_vaca', 'caliente'),
    serie('w-leche-bufala', 'lacteos', 'leche_bufala', null),
    comparador('w-comparador-queso', 'lacteos', 'queso', null),
    movers('w-movers-lacteos', 'lacteos'),
    calculadora('leche'),
    alertas(),
    favoritos(),
  ],
  porcicultor: () => [
    bcv(),
    serie('w-cerdo-industrial-pie', 'porcino', 'cerdo_industrial', 'en_pie'),
    noticias(),
    serie('w-cerda-pie', 'porcino', 'cerda', 'en_pie'),
    serie('w-lechon-pie', 'porcino', 'lechon', 'en_pie'),
    serie('w-cerdo-pie', 'porcino', 'cerdo', 'en_pie'),
    comparador('w-comparador-cerdo', 'porcino', 'cerdo', 'en_pie'),
    movers('w-movers-porcino', 'porcino'),
    calculadora('lote'),
    alertas(),
    favoritos(),
  ],
  bufalero: () => [
    bcv(),
    serie('w-bufalo-pie', 'bufalino', 'bufalo', 'en_pie'),
    noticias(),
    serie('w-bufala-pie', 'bufalino', 'bufala', 'en_pie'),
    serie('w-baute-pie', 'bufalino', 'baute', 'en_pie'),
    serie('w-leche-bufala', 'lacteos', 'leche_bufala', null),
    comparador('w-comparador-bufalo', 'bufalino', 'bufalo', 'en_pie'),
    movers('w-movers-bufalino', 'bufalino'),
    calculadora('lote'),
    alertas(),
    favoritos(),
  ],
  agricultor: () => [
    bcv(),
    serie('w-papa-productor', 'hortalizas', 'papa', 'productor'),
    noticias(),
    serie('w-cebolla-productor', 'hortalizas', 'cebolla', 'productor'),
    serie('w-tomate-productor', 'hortalizas', 'tomate_perita', 'productor'),
    serie('w-zanahoria-productor', 'hortalizas', 'zanahoria', 'productor'),
    serie('w-aguacate-productor', 'frutas', 'aguacate', 'productor'),
    serie('w-fresa-productor', 'frutas', 'fresa', 'productor'),
    movers('w-movers-hortalizas', 'hortalizas'),
    alertas(),
    favoritos(),
    fuentes(),
  ],
  comerciante: () => [
    bcv(),
    serie('w-toro-canal', 'bovino', 'toro', 'en_canal'),
    serie('w-cerdo-industrial-canal', 'porcino', 'cerdo_industrial', 'en_canal'),
    serie('w-papa-consumidor', 'hortalizas', 'papa', 'consumidor'),
    serie('w-papa-productor', 'hortalizas', 'papa', 'productor'),
    serie('w-tomate-consumidor', 'hortalizas', 'tomate_perita', 'consumidor'),
    serie('w-queso', 'lacteos', 'queso', null),
    movers('w-movers-hortalizas', 'hortalizas'),
    calculadora('conversor'),
    alertas(),
    favoritos(),
    resumen(false),
  ],
};

/** Copia nueva de los widgets del perfil (nunca comparte referencias entre llamadas). */
export const widgetsDePerfil = (perfil: PerfilTipo): WidgetConfig[] => PLANTILLAS[perfil]();
